import { createClient } from '@supabase/supabase-js';
import { createSignal } from 'solid-js';

/**
 * Supabase, straight from the browser.
 *
 * This replaced Turso. Turso had no notion of a user: the credentials were
 * inlined in the bundle and everyone who loaded the page shared one database
 * with full read and write. That was a deliberate trade while there was no
 * sign-in, and it stopped being acceptable the moment there was.
 *
 * Here the publishable key is still in the bundle and is still meant to be —
 * it identifies the project, not the person. What separates one user's rows
 * from another's is row-level security on the session's `auth.uid()`, enforced
 * by the database rather than by anything in this file.
 */

const url = import.meta.env.VITE_SUPABASE_URL;
const key = import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY || import.meta.env.VITE_SUPABASE_ANON_KEY;
const setup = 'See app/.env.example. Values are in Supabase → Project Settings → API.';

if (!url) throw new Error(`VITE_SUPABASE_URL is not set. ${setup}`);
if (url.startsWith('sb_') || url.startsWith('eyJ')) {
    throw new Error(`VITE_SUPABASE_URL holds a key, not a URL. It wants the Project URL — https://<project-ref>.supabase.co. ${setup}`);
}
if (!key) throw new Error(`VITE_SUPABASE_PUBLISHABLE_KEY is not set. ${setup}`);

/**
 * The secret key bypasses row-level security entirely. In a browser bundle it
 * would hand every visitor every user's data, and nothing else here would look
 * wrong — so it is refused rather than trusted.
 */
const isSecret = key.startsWith('sb_secret_') || (key.startsWith('eyJ') && atob(key.split('.')[1] ?? '').includes('service_role'));
if (isSecret) {
    throw new Error(
        'Refusing to start: that is the secret (service_role) key, which bypasses row-level security and must never reach a browser. ' +
            `Use the publishable key instead, and rotate the secret one. ${setup}`,
    );
}

export const db = createClient(url, key);

export const configured = Boolean(url && key);

/** Where to send someone who is not signed in. Auth lives on the marketing
    site, which is the same origin in production. */
export const LOGIN = `${import.meta.env.VITE_SITE_URL ?? ''}/login`;

// One shared surface for failures. Nothing here throws at the call site: a
// dropped write is reported, not swallowed, but it must not tear down a
// keystroke handler mid-edit.
const [dbError, setDbError] = createSignal<string | null>(null);
export { dbError, setDbError };

export const reportError = (context: string, e: unknown): void => {
    const message = e instanceof Error ? e.message : String(e);
    console.error(`${context}:`, e);
    setDbError(`${context}: ${message}`);
};

/**
 * Unwrap a Supabase response, reporting rather than throwing.
 *
 * Supabase returns errors in the body instead of rejecting, so a `try/catch`
 * around a query catches nothing and the failure reads as an empty result.
 * Everything goes through here so that cannot happen quietly.
 */
export const unwrap = <T>(context: string, res: { data: T | null; error: { message: string } | null }): T | null => {
    if (res.error) {
        reportError(context, new Error(res.error.message));
        return null;
    }
    return res.data;
};

// Field edits fire on every keystroke. Without this, typing "1200" into a
// balance is four round trips and the last one to land wins — which is not
// necessarily the last one sent. Coalescing per field makes it one write of the
// final value.
interface Pending {
    timer: ReturnType<typeof setTimeout>;
    /**
     * PromiseLike, not Promise. A Supabase query builder is thenable but is not
     * a Promise instance — it only runs when awaited — so requiring a Promise
     * would mean wrapping every call site in an async arrow for nothing.
     */
    run: () => PromiseLike<unknown>;
}

const pending = new Map<string, Pending>();

const fire = (key: string): void => {
    const entry = pending.get(key);
    if (!entry) return;
    clearTimeout(entry.timer);
    pending.delete(key);
    // Supabase reports failures in the body rather than rejecting, so the catch
    // alone would miss them.
    void Promise.resolve(entry.run())
        .then((res) => {
            const error = (res as { error?: { message: string } } | null)?.error;
            if (error) reportError(`saving ${key}`, new Error(error.message));
        })
        .catch((e) => reportError(`saving ${key}`, e));
};

export const debouncedWrite = (key: string, run: () => PromiseLike<unknown>, delay = 400): void => {
    const existing = pending.get(key);
    if (existing) clearTimeout(existing.timer);
    pending.set(key, { run, timer: setTimeout(() => fire(key), delay) });
};

/** Run every pending write now rather than waiting out its delay. Bound to page
    hide, so a value typed and immediately navigated away from still lands. */
export const flushWrites = (): void => {
    for (const key of [...pending.keys()]) fire(key);
};

if (typeof document !== 'undefined') {
    // visibilitychange, not beforeunload: it is the one that reliably fires on
    // mobile and on tab close.
    document.addEventListener('visibilitychange', () => {
        if (document.visibilityState === 'hidden') flushWrites();
    });
}
